import { useEffect, useRef } from 'react';
import { usePostHog } from '@posthog/react';
import useAuth from '../common/hooks/useAuth';
import { useAxiosWithAuth } from '../common/api/hooks/useAxiosWithAuth';
import { URL } from '../common/api/urls';
import { isPostHogEnabled } from './posthog';
import { getEventUserProperties, getSubscriptionPersonProperties } from './userProperties';
import { clearAnalyticsUserProperties, setAnalyticsUserProperties } from './chartIdentity';
import { captureChartsSessionStarted, clearChartsSessionFired } from './chartsSession';
import { endChartsToolSession } from './chartsTool';
import { getAttributionApiPayload, applyServerHandoff, getHandoffPayloadToSync } from './chartsAttribution';

const SUPER_PROPERTIES = ['role', 'user_tier', 'plan_tier', 'subscription_status'];

const PostHogAnalytics = () => {
  const posthog = usePostHog();
  const { auth } = useAuth();
  const previousUserIdRef = useRef(null);
  const [
    ,
    subscriptionResponse,
    subscriptionError,
    subscriptionLoaded,
    resetSubscription,
    fetchSubscription,
  ] = useAxiosWithAuth();

  const [, , , , , saveHandoff] = useAxiosWithAuth();

  const roleKey = Array.isArray(auth?.role) ? auth.role.join(',') : '';
  const resourceKey = Array.isArray(auth?.resources) ? auth.resources.join(',') : '';

  useEffect(() => {
    if (!auth?.accessToken) {
      return;
    }
    applyServerHandoff(auth.handoff);
    const payload = getHandoffPayloadToSync(auth.handoff);
    if (payload) {
      saveHandoff(URL.HANDOFF, 'POST', payload);
    }
  }, [auth?.accessToken, auth?.handoff]);

  useEffect(() => {
    if (!isPostHogEnabled || !posthog) {
      return;
    }

    if (auth?.accessToken) {
      fetchSubscription(URL.USER_SUBSCRIPTION, 'GET', {});
      return;
    }

    if (previousUserIdRef.current) {
      SUPER_PROPERTIES.forEach((key) => posthog.unregister(key));
      endChartsToolSession('logout');
      clearAnalyticsUserProperties();
      clearChartsSessionFired();
      posthog.reset();
      previousUserIdRef.current = null;
    }
  }, [auth?.accessToken, roleKey, resourceKey, posthog]);

  useEffect(() => {
    if (!isPostHogEnabled || !posthog || !subscriptionLoaded) {
      return;
    }

    if (subscriptionError || !subscriptionResponse?.data) {
      resetSubscription();
      return;
    }

    const profile = subscriptionResponse.data;
    const userId = String(profile.user_id ?? profile.id ?? '');
    if (!userId) {
      resetSubscription();
      return;
    }

    const eventProperties = getEventUserProperties({
      ...auth,
      ...profile,
      subscriber: profile.subscriber,
    });

    setAnalyticsUserProperties(eventProperties);
    posthog.register(eventProperties);
    const attributionPayload = getAttributionApiPayload();
    posthog.identify(userId, {
      plan_tier: eventProperties.plan_tier,
      subscription_status: eventProperties.subscription_status,
      ...(profile.created_at ? { account_created_at: profile.created_at } : {}),
      ...getSubscriptionPersonProperties(profile),
      ...(attributionPayload?.first_handoff_id ? { first_handoff_id: attributionPayload.first_handoff_id } : {}),
      ...(attributionPayload?.last_handoff_id ? { last_handoff_id: attributionPayload.last_handoff_id } : {}),
    });
    captureChartsSessionStarted(posthog, {
      plan_tier: eventProperties.plan_tier,
      subscription_status: eventProperties.subscription_status,
    });
    previousUserIdRef.current = userId;
    resetSubscription();
  }, [auth, posthog, subscriptionLoaded, subscriptionResponse, subscriptionError, resetSubscription]);

  return null;
};

export default PostHogAnalytics;
